import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage, permLabel } from '../api/client';
import type { AclEntry } from '../api/types';

const inputCls =
  'rounded border border-gray-300 px-2.5 py-1.5 text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy';
const labelCls = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500';

/** ISO date-time -> value for an <input type="datetime-local"> in the viewer's zone. */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtDateTime(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString() : '—';
}

/** The identity fields that apply to this entry, as label/value pairs. */
function principalFields(entry: AclEntry): Array<{ label: string; value: string }> {
  if (entry.principal_type === 'apikey') {
    return [{ label: 'Key prefix', value: `${entry.key_prefix ?? ''}…` }];
  }
  const fields: Array<{ label: string; value: string | null | undefined }> = [
    { label: 'Email', value: entry.oauth_email },
    { label: 'Subject (oid)', value: entry.oauth_subject },
    { label: 'Tenant', value: entry.oauth_tenant },
    { label: 'App ID', value: entry.oauth_app_id },
    { label: 'Audience', value: entry.oauth_audience },
    { label: 'Issuer', value: entry.oauth_issuer },
  ];
  return fields.filter((f): f is { label: string; value: string } => !!f.value);
}

/**
 * Expanded detail panel for one ACL entry: full principal fields, its grants,
 * rename, expiration set/clear, and revoke. Rendered inside the ACL table as an
 * expanded row; mutations invalidate the 'acl' query so the grid refreshes.
 */
export function AclEntryDetails({ entry, onClose }: { entry: AclEntry; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(entry.name);
  const [expires, setExpires] = useState(entry.expires_utc ? toLocalInput(entry.expires_utc) : '');

  const patch = useMutation({
    mutationFn: (body: { name?: string; expiresUtc?: string | null }) =>
      api(`/api/admin/acl/${entry.id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['acl'] }),
  });

  const revoke = useMutation({
    mutationFn: () => api(`/api/admin/acl/${entry.id}/revoke`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['acl'] }),
  });

  const nameChanged = name.trim().length > 0 && name.trim() !== entry.name;
  const expiresIso = expires ? new Date(expires).toISOString() : null;
  const expiresChanged = expiresIso !== (entry.expires_utc ?? null);

  return (
    <div className="border-l-4 border-navy/30 bg-gray-50 p-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <div className={labelCls}>Details</div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-gray-500">Type</dt>
            <dd>{entry.principal_type}</dd>
            {principalFields(entry).map((f) => (
              <div key={f.label} className="contents">
                <dt className="text-gray-500">{f.label}</dt>
                <dd className="break-all font-mono text-xs leading-5">{f.value}</dd>
              </div>
            ))}
            <dt className="text-gray-500">Created</dt>
            <dd>{fmtDateTime(entry.created_utc)}</dd>
            <dt className="text-gray-500">Last used</dt>
            <dd>{fmtDateTime(entry.last_used_utc)}</dd>
            <dt className="text-gray-500">Expires</dt>
            <dd>{entry.expires_utc ? fmtDateTime(entry.expires_utc) : 'never'}</dd>
          </dl>

          <div className={`${labelCls} mt-4`}>Grants</div>
          {(entry.grants ?? []).length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500">
                  <th className="py-1 pr-3">Bucket</th>
                  <th className="py-1 pr-3">Path</th>
                  <th className="py-1">Permissions</th>
                </tr>
              </thead>
              <tbody>
                {(entry.grants ?? []).map((g) => (
                  <tr key={g.id} className="border-t border-gray-200">
                    <td className="py-1 pr-3 font-mono text-xs">{g.bucket}</td>
                    <td className="py-1 pr-3 font-mono text-xs">{g.path_prefix || '(whole bucket)'}</td>
                    <td className="py-1 text-xs">{permLabel(g.perms)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-gray-500">No grants — this entry confers no access.</p>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className={labelCls}>Name</label>
            <div className="flex gap-2">
              <input className={`${inputCls} flex-1`} value={name} onChange={(e) => setName(e.target.value)} />
              <button
                className="rounded bg-navy px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                disabled={!nameChanged || patch.isPending}
                onClick={() => patch.mutate({ name: name.trim() })}
              >
                Rename
              </button>
            </div>
          </div>

          <div>
            <label className={labelCls}>Expiration</label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="datetime-local"
                className={inputCls}
                value={expires}
                onChange={(e) => setExpires(e.target.value)}
              />
              <button
                className="rounded bg-navy px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                disabled={!expires || !expiresChanged || patch.isPending}
                onClick={() => patch.mutate({ expiresUtc: expiresIso })}
              >
                Set
              </button>
              {entry.expires_utc ? (
                <button
                  className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-white disabled:opacity-50"
                  disabled={patch.isPending}
                  onClick={() => {
                    setExpires('');
                    patch.mutate({ expiresUtc: null });
                  }}
                >
                  Clear
                </button>
              ) : null}
            </div>
            <p className="mt-1 text-xs text-gray-500">An expired entry stops authorizing immediately; it is not revoked and the date can be extended.</p>
          </div>

          <div className="flex gap-2 pt-1">
            {!entry.revoked ? (
              <button
                className="rounded border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate()}
              >
                {revoke.isPending ? 'Revoking…' : 'Revoke'}
              </button>
            ) : null}
            <button className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-white" onClick={onClose}>
              Close
            </button>
          </div>

          {patch.isError ? <p className="text-sm text-red-600">{getErrorMessage(patch.error)}</p> : null}
          {revoke.isError ? <p className="text-sm text-red-600">{getErrorMessage(revoke.error)}</p> : null}
        </div>
      </div>
    </div>
  );
}
